// Lee los adjuntos nativos del correo abierto en Outlook (Office.js) y los expone como
// File del navegador, para poder reusar el mismo pipeline de extracción de texto
// (buildFileContext / extractFileText) que ya usa el clip de subida manual en ChatTab.
// Requiere solo el permiso ReadWriteItem que el manifest ya declara — no usa Graph.

export interface EmailAttachmentMeta {
  id: string;
  name: string;
  size: number;
  contentType: string;
}

export function getEmailAttachments(): EmailAttachmentMeta[] {
  const item = Office.context?.mailbox?.item as any;
  const attachments = item?.attachments as Office.AttachmentDetails[] | undefined;
  if (!attachments) return [];

  return attachments
    .filter(a => a.attachmentType === Office.MailboxEnums.AttachmentType.File && !a.isInline)
    .map(a => ({ id: a.id, name: a.name, size: a.size, contentType: a.contentType }));
}

function base64ToFile(base64: string, name: string, contentType: string): File {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], name, { type: contentType || 'application/octet-stream' });
}

function fetchAttachmentContent(id: string): Promise<Office.AttachmentContent> {
  return new Promise((resolve, reject) => {
    const item = Office.context?.mailbox?.item as any;
    item.getAttachmentContentAsync(id, (result: Office.AsyncResult<Office.AttachmentContent>) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value);
      } else {
        reject(result.error);
      }
    });
  });
}

// Descarga todos los adjuntos indicados y los devuelve como File[]; los que fallen (p.ej.
// adjuntos en la nube sin contenido binario recuperable) se omiten en vez de abortar el resto.
export async function buildEmailAttachmentsFiles(metas: EmailAttachmentMeta[]): Promise<File[]> {
  const files: File[] = [];
  for (const meta of metas) {
    try {
      const content = await fetchAttachmentContent(meta.id);
      if (content.format !== Office.MailboxEnums.AttachmentContentFormat.Base64) continue;
      files.push(base64ToFile(content.content, meta.name, meta.contentType));
    } catch {
      // Se omite silenciosamente: el bloque de contexto ya deja constancia del nombre/tamaño
      // vía EmailPreview, y buildFileContext solo procesa lo que sí se pudo descargar.
    }
  }
  return files;
}
